import { defineStore } from 'pinia'
import { ethers } from 'ethers'
import { t } from '../i18n'
import { queryIndexer } from '../utils/api'

const STAKE_ADDRESS = process.env.VUE_APP_STAKE_ADDRESS || ''
const TOKEN_ADDRESS = process.env.VUE_APP_TOKEN_ADDRESS || ''
const CHAIN_ID = Number(process.env.VUE_APP_CHAIN_ID || 56)
const SCAN_FROM_BLOCK = Number(process.env.VUE_APP_SCAN_FROM_BLOCK || 0)
const INVITE_AMOUNT = ethers.utils.parseEther('0.2')
const CONFIRM_AMOUNT = ethers.utils.parseEther('0.1')
const USER_CHUNK = 12
const TEAM_PAGE_SIZE = 1000

// 3-gen tree from the Goldsky subgraph: one request covers gen1 + gen2 + gen3.
const TEAM_QUERY = `query Team($referrer: ID!, $first: Int!) {
  account(id: $referrer) {
    teamDepositBNB
    directs(first: $first) {
      id
      totalDepositBNB
      depositValueU
      directs(first: $first) {
        id
        totalDepositBNB
        depositValueU
        directs(first: $first) {
          id
          totalDepositBNB
          depositValueU
        }
      }
    }
  }
}`

const STAKE_ABI = [
  'function users(address) view returns (uint256 totalDepositBNB, uint256 depositValueU, uint256 exitQuotaU, uint256 lastStaticAt, uint256 pendingStaticU, uint256 pendingGlobalU, uint256 globalRewardDebt, uint32 activeDirects, address countedReferrer)',
  'function pendingStaticU(address account) view returns (uint256)',
  'function pendingGlobalU(address account) view returns (uint256)',
  'function unlockedLevels(address account) view returns (uint256)',
  'function eligibleNft(address account) view returns (bool)',
  'function eligibleNftShares(address account) view returns (uint256)',
  'function nftRewardDebt(address account) view returns (uint256)',
  'function accNftRewardPerToken() view returns (uint256)',
  'function uToDK(uint256 amountU) view returns (uint256)',
  'function bnbUsdPrice() view returns (uint256)',
]

const TOKEN_ABI = [
  'function balanceOf(address account) view returns (uint256)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function getReferrer(address user) view returns (address)',
  'function hasInvited(address user, address inviter) view returns (bool)',
  'function root() view returns (address)',
  'event RelationBound(address indexed user, address indexed referrer)',
]

const emptyStake = () => ({
  totalDepositBNB: '0',
  depositValueU: '0',
  exitQuotaU: '0',
  lastStaticClaimAt: 0,
  activeDirects: 0,
  unlockedLevels: 0,
  countedReferrer: '',
})

const emptyReward = (unit = 'DK') => ({ amount: '0', unit })
const emptyPending = () => ({
  static: emptyReward('DK'),
  global: emptyReward('BNB'),
  nft: emptyReward('BNB'),
  totalBnb: '0',
  totalDk: '0',
})

function isAddress(value) {
  return Boolean(value) && ethers.utils.isAddress(value)
}

function zeroAddress(value) {
  return !value || value === ethers.constants.AddressZero
}

function formatUnits(value) {
  try {
    return ethers.utils.formatEther(value || 0)
  } catch (_) {
    return '0'
  }
}

function parseContractError(error) {
  const text = [
    error?.error?.data?.message,
    error?.error?.message,
    error?.data?.message,
    error?.reason,
    error?.message,
  ].filter(Boolean).join(' ')
  if (/NoReward/i.test(text)) return t('error.noReward')
  if (/NftNotRegistered/i.test(text)) return t('error.nftNotRegistered')
  if (/insufficient funds|insufficient balance/i.test(text)) return t('error.insufficient')
  if (/user rejected|denied/i.test(text)) return t('error.rejected')
  if (/relation cycle/i.test(text)) return t('error.relationCycle')
  if (/transfer amount exceeds balance/i.test(text)) return t('error.insufficientDk')
  return error?.reason || error?.message || t('error.txFailed')
}

async function mapChunk(items, fn) {
  const result = []
  for (let index = 0; index < items.length; index += USER_CHUNK) {
    const slice = items.slice(index, index + USER_CHUNK)
    result.push(...await Promise.all(slice.map(fn)))
  }
  return result
}

export const useStakeStore = defineStore('stake', {
  state: () => ({
    walletAddress: '',
    provider: null,
    signer: null,
    walletBalance: '--',
    dkBalance: '--',
    boundReferrer: '',
    rootAddress: '',
    stake: emptyStake(),
    pending: emptyPending(),
    generations: { 1: [], 2: [], 3: [] },
    generationTotals: { 1: '0', 2: '0', 3: '0' },
    teamTotalBNB: '0',
    wholeTeamPerformanceBNB: null,
    teamCounts: { 1: 0, 2: 0, 3: 0 },
    teamLoading: false,
    teamFromChain: false,
    stakeLoading: false,
    claimPending: '',
    invitePending: false,
    confirmPending: false,
  }),
  getters: {
    canClaimKind: state => kind => Number(state.pending[kind]?.amount || 0) > 0,
    // 与 DKToken._handleRelation 一致：已绑定上级，或发起方为 root
    canInvite: state => {
      if (!state.walletAddress) return false
      if (state.boundReferrer) return true
      return Boolean(state.rootAddress) && state.walletAddress.toLowerCase() === state.rootAddress.toLowerCase()
    },
  },
  actions: {
    tokenContract(runner = this.signer || this.provider) {
      if (!runner || !isAddress(TOKEN_ADDRESS)) throw new Error(t('error.noToken'))
      return new ethers.Contract(TOKEN_ADDRESS, TOKEN_ABI, runner)
    },
    stakeContract(runner = this.signer || this.provider) {
      if (!runner || !isAddress(STAKE_ADDRESS)) throw new Error(t('error.noStake'))
      return new ethers.Contract(STAKE_ADDRESS, STAKE_ABI, runner)
    },
    async connect() {
      if (!window.ethereum) throw new Error(t('error.needWeb3'))
      this.provider = new ethers.providers.Web3Provider(window.ethereum, 'any')
      const accounts = await this.provider.send('eth_requestAccounts', [])
      if (!accounts.length) throw new Error(t('error.noAccount'))
      const network = await this.provider.getNetwork()
      if (CHAIN_ID && network.chainId !== CHAIN_ID) throw new Error(t('error.wrongNetwork', { chainId: CHAIN_ID }))
      this.signer = this.provider.getSigner()
      this.bindWalletEvents()
      this.activateWallet(accounts[0])
      await this.refreshAccount()
      return { address: this.walletAddress }
    },
    async restore() {
      if (!window.ethereum) return
      if (window.localStorage.getItem('dk_wallet_disconnected') === 'true') return
      this.provider = new ethers.providers.Web3Provider(window.ethereum, 'any')
      const accounts = await this.provider.listAccounts()
      if (!accounts.length) return
      const network = await this.provider.getNetwork()
      if (CHAIN_ID && network.chainId !== CHAIN_ID) return
      this.signer = this.provider.getSigner()
      this.bindWalletEvents()
      this.activateWallet(accounts[0])
      await this.refreshAccount()
    },
    bindWalletEvents() {
      if (!window.ethereum || window.ethereum.__dkStakeBound) return
      window.ethereum.__dkStakeBound = true
      window.ethereum.on('accountsChanged', async accounts => {
        if (!accounts.length) {
          this.clearWalletState()
          return
        }
        if (window.localStorage.getItem('dk_wallet_disconnected') === 'true') return
        this.signer = this.provider.getSigner()
        this.activateWallet(accounts[0])
        await this.refreshAccount()
      })
      window.ethereum.on('chainChanged', () => window.location.reload())
    },
    activateWallet(address) {
      this.walletAddress = ethers.utils.getAddress(address)
      window.localStorage.removeItem('dk_wallet_disconnected')
    },
    clearWalletState() {
      this.walletAddress = ''
      this.provider = null
      this.signer = null
      this.walletBalance = '--'
      this.dkBalance = '--'
      this.boundReferrer = ''
      this.rootAddress = ''
      this.stake = emptyStake()
      this.pending = emptyPending()
      this.generations = { 1: [], 2: [], 3: [] }
      this.generationTotals = { 1: '0', 2: '0', 3: '0' }
      this.teamTotalBNB = '0'
      this.wholeTeamPerformanceBNB = null
      this.teamCounts = { 1: 0, 2: 0, 3: 0 }
      this.teamFromChain = false
      this.claimPending = ''
      this.invitePending = false
      this.confirmPending = false
    },
    async disconnect() {
      if (window.ethereum && window.ethereum.request) {
        try {
          await window.ethereum.request({ method: 'wallet_revokePermissions', params: [{ eth_accounts: {} }] })
        } catch (_) { /* not supported by every injected wallet */ }
      }
      window.localStorage.setItem('dk_wallet_disconnected', 'true')
      this.clearWalletState()
    },
    async refreshAccount() {
      if (!this.walletAddress) return
      await Promise.all([this.loadWalletMetrics(), this.loadRelation(), this.loadStake(), this.loadGenerations()])
    },
    async loadWalletMetrics() {
      if (!this.provider || !this.walletAddress) return
      try {
        const balance = await this.provider.getBalance(this.walletAddress)
        this.walletBalance = Number(ethers.utils.formatEther(balance)).toFixed(4)
      } catch (_) {
        this.walletBalance = '--'
      }
      try {
        const token = this.tokenContract(this.provider)
        const dk = await token.balanceOf(this.walletAddress)
        this.dkBalance = Number(ethers.utils.formatEther(dk)).toFixed(4)
      } catch (_) {
        this.dkBalance = '--'
      }
    },
    async loadRelation() {
      if (!this.provider || !this.walletAddress) return
      try {
        const token = this.tokenContract(this.provider)
        const [bound, root] = await Promise.all([
          token.getReferrer(this.walletAddress),
          token.root().catch(() => ethers.constants.AddressZero),
        ])
        this.boundReferrer = zeroAddress(bound) ? '' : ethers.utils.getAddress(bound)
        this.rootAddress = zeroAddress(root) ? '' : ethers.utils.getAddress(root)
      } catch (_) {
        this.boundReferrer = ''
        this.rootAddress = ''
      }
    },
    async pendingNftU(account, stake) {
      const [sharesRaw, flagged, acc, debt] = await Promise.all([
        stake.eligibleNftShares(account),
        stake.eligibleNft(account),
        stake.accNftRewardPerToken(),
        stake.nftRewardDebt(account),
      ])
      const shares = sharesRaw.gt(0) ? sharesRaw : (flagged ? ethers.BigNumber.from(1) : ethers.BigNumber.from(0))
      if (shares.isZero()) return ethers.BigNumber.from(0)
      const accumulated = shares.mul(acc)
      return accumulated.gt(debt) ? accumulated.sub(debt) : ethers.BigNumber.from(0)
    },
    async quoteDk(stake, amountU) {
      if (!amountU || amountU.isZero()) return '0'
      try {
        return formatUnits(await stake.uToDK(amountU))
      } catch (_) {
        return '0'
      }
    },
    async quoteBnb(amountU, bnbPrice) {
      if (!amountU || amountU.isZero() || !bnbPrice || bnbPrice.isZero()) return '0'
      try {
        return formatUnits(amountU.mul(ethers.constants.WeiPerEther).div(bnbPrice))
      } catch (_) {
        return '0'
      }
    },
    async loadStake() {
      if (!this.provider || !this.walletAddress) return
      this.stakeLoading = true
      try {
        const stake = this.stakeContract(this.provider)
        const [user, staticU, globalU, levels, nftU, bnbPrice] = await Promise.all([
          stake.users(this.walletAddress),
          stake.pendingStaticU(this.walletAddress),
          stake.pendingGlobalU(this.walletAddress),
          stake.unlockedLevels(this.walletAddress),
          this.pendingNftU(this.walletAddress, stake),
          stake.bnbUsdPrice().catch(() => ethers.BigNumber.from(0)),
        ])
        this.stake = {
          totalDepositBNB: formatUnits(user.totalDepositBNB),
          depositValueU: formatUnits(user.depositValueU),
          exitQuotaU: formatUnits(user.exitQuotaU),
          lastStaticClaimAt: Number(user.lastStaticAt || 0),
          activeDirects: Number(user.activeDirects || 0),
          unlockedLevels: Number(levels || 0),
          countedReferrer: zeroAddress(user.countedReferrer) ? '' : ethers.utils.getAddress(user.countedReferrer),
        }
        // Contract payout: static → DK; global/NFT → BNB.
        const [staticDk, globalBnb, nftBnb] = await Promise.all([
          this.quoteDk(stake, staticU),
          this.quoteBnb(globalU, bnbPrice),
          this.quoteBnb(nftU, bnbPrice),
        ])
        const totalBnb = ethers.utils.parseEther(globalBnb).add(ethers.utils.parseEther(nftBnb))
        this.pending = {
          static: { amount: staticDk, unit: 'DK' },
          global: { amount: globalBnb, unit: 'BNB' },
          nft: { amount: nftBnb, unit: 'BNB' },
          totalBnb: formatUnits(totalBnb),
          totalDk: staticDk,
        }
      } catch (_) {
        this.stake = emptyStake()
        this.pending = emptyPending()
      } finally {
        this.stakeLoading = false
      }
    },
    async loadDirects(referrer) {
      const token = this.tokenContract(this.provider)
      const filter = token.filters.RelationBound(null, referrer)
      const logs = await token.queryFilter(filter, SCAN_FROM_BLOCK || undefined)
      const seen = new Set()
      const addresses = []
      logs.forEach(log => {
        const user = ethers.utils.getAddress(log.args.user)
        if (seen.has(user)) return
        seen.add(user)
        addresses.push(user)
      })
      return addresses
    },
    async decorateMembers(addresses, generation) {
      if (!addresses.length) return []
      const stake = this.stakeContract(this.provider)
      return mapChunk(addresses, async address => {
        try {
          const user = await stake.users(address)
          return {
            address,
            generation,
            depositBNB: formatUnits(user.totalDepositBNB),
            depositValueU: formatUnits(user.depositValueU),
            exitQuotaU: formatUnits(user.exitQuotaU),
          }
        } catch (_) {
          return { address, generation, depositBNB: '0', depositValueU: '0', exitQuotaU: '0' }
        }
      })
    },
    sumDeposit(list) {
      return list.reduce((total, item) => total.add(ethers.utils.parseEther(item.depositBNB || '0')), ethers.BigNumber.from(0))
    },
    async loadGenerations() {
      if (!this.walletAddress) return
      this.teamLoading = true
      try {
        let lists
        try {
          lists = await this.loadTeamFromIndexer(this.walletAddress)
        } catch (_) {
          lists = await this.loadTeamFromRpc(this.walletAddress)
        }
        this.applyGenerations(lists)
        this.teamFromChain = true
      } catch (_) {
        this.applyGenerations({ 1: [], 2: [], 3: [] })
        this.teamFromChain = false
      } finally {
        this.teamLoading = false
      }
    },
    applyGenerations(lists) {
      const list1 = lists[1] || []
      const list2 = lists[2] || []
      const list3 = lists[3] || []
      const total1 = this.sumDeposit(list1)
      const total2 = this.sumDeposit(list2)
      const total3 = this.sumDeposit(list3)
      this.generations = { 1: list1, 2: list2, 3: list3 }
      this.generationTotals = {
        1: formatUnits(total1),
        2: formatUnits(total2),
        3: formatUnits(total3),
      }
      this.teamCounts = { 1: list1.length, 2: list2.length, 3: list3.length }
      this.teamTotalBNB = formatUnits(total1.add(total2).add(total3))
      this.wholeTeamPerformanceBNB = lists.teamDepositBNB === undefined
        ? null
        : formatUnits(lists.teamDepositBNB)
    },
    indexerMembers(nodes, generation) {
      return (nodes || []).map(node => ({
        address: ethers.utils.getAddress(node.id),
        generation,
        depositBNB: formatUnits(node.totalDepositBNB),
        depositValueU: formatUnits(node.depositValueU),
      }))
    },
    async loadTeamFromIndexer(address) {
      const data = await queryIndexer(TEAM_QUERY, { referrer: address.toLowerCase(), first: TEAM_PAGE_SIZE })
      const gen1 = (data && data.account && data.account.directs) || []
      const gen2 = gen1.reduce((list, node) => list.concat(node.directs || []), [])
      const gen3 = gen2.reduce((list, node) => list.concat(node.directs || []), [])
      return {
        1: this.indexerMembers(gen1, 1),
        2: this.indexerMembers(gen2, 2),
        3: this.indexerMembers(gen3, 3),
        teamDepositBNB: data && data.account ? data.account.teamDepositBNB : '0',
      }
    },
    async loadTeamFromRpc(address) {
      if (!this.provider || !isAddress(TOKEN_ADDRESS)) throw new Error(t('error.noToken'))
      const gen1 = await this.loadDirects(address)
      const gen2 = (await mapChunk(gen1, item => this.loadDirects(item))).flat()
      const gen3 = (await mapChunk(gen2, item => this.loadDirects(item))).flat()
      const [list1, list2, list3] = await Promise.all([
        this.decorateMembers(gen1, 1),
        this.decorateMembers(gen2, 2),
        this.decorateMembers(gen3, 3),
      ])
      return { 1: list1, 2: list2, 3: list3 }
    },
    async sendInvite(address) {
      if (!this.signer || !this.walletAddress) throw new Error(t('error.reconnect'))
      if (!isAddress(address)) throw new Error(t('error.invalidAddress'))
      const friend = ethers.utils.getAddress(address)
      if (friend.toLowerCase() === this.walletAddress.toLowerCase()) throw new Error(t('error.selfReferrer'))
      const token = this.tokenContract(this.signer)
      const already = await token.getReferrer(friend)
      if (!zeroAddress(already)) {
        throw new Error(t('error.alreadyBound'))
      }
      this.invitePending = true
      try {
        const transaction = await token.transfer(friend, INVITE_AMOUNT)
        await transaction.wait(1)
        await this.loadWalletMetrics()
        return transaction.hash
      } catch (error) {
        throw new Error(parseContractError(error))
      } finally {
        this.invitePending = false
      }
    },
    async confirmInvite(address) {
      if (!this.signer || !this.walletAddress) throw new Error(t('error.reconnect'))
      if (!isAddress(address)) throw new Error(t('error.invalidAddress'))
      const inviter = ethers.utils.getAddress(address)
      if (inviter.toLowerCase() === this.walletAddress.toLowerCase()) throw new Error(t('error.selfReferrer'))
      const token = this.tokenContract(this.signer)
      const already = await token.getReferrer(this.walletAddress)
      if (!zeroAddress(already)) {
        this.boundReferrer = ethers.utils.getAddress(already)
        return already
      }
      const invited = await token.hasInvited(this.walletAddress, inviter)
      if (!invited) throw new Error(t('error.waitInvite'))
      this.confirmPending = true
      try {
        const transaction = await token.transfer(inviter, CONFIRM_AMOUNT)
        await transaction.wait(1)
        await Promise.all([this.loadWalletMetrics(), this.loadRelation(), this.loadStake()])
        return transaction.hash
      } catch (error) {
        throw new Error(parseContractError(error))
      } finally {
        this.confirmPending = false
      }
    },
    async claimReward(kind) {
      if (!this.signer || !this.walletAddress) throw new Error(t('error.reconnect'))
      const triggers = {
        static: ethers.constants.Zero,
        global: ethers.utils.parseEther('0.00001'),
        nft: ethers.utils.parseEther('0.00002'),
      }
      const trigger = triggers[kind]
      if (!trigger && kind !== 'static') throw new Error(t('error.noReward'))
      if (!this.canClaimKind(kind)) throw new Error(t('error.noReward'))
      this.claimPending = kind
      try {
        const transaction = await this.signer.sendTransaction({
          to: STAKE_ADDRESS,
          value: trigger,
        })
        await transaction.wait(1)
        await Promise.all([this.loadWalletMetrics(), this.loadStake()])
        return transaction.hash
      } catch (error) {
        throw new Error(parseContractError(error))
      } finally {
        this.claimPending = ''
      }
    },
  },
})
